import { Link } from "react-router-dom";
import { ScrollText, Mail, Phone, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BRAND } from "@/lib/data";

export default function Terms() {
  const updated = "July 2026";
  return (
    <main data-testid="terms-page" className="pt-32 pb-28 relative z-10 bg-[#F9F8F6]">
      <Seo
        title="Terms of Service | ZEBO India Private Limited"
        description="The terms that apply when you use ZEBO's website, book a design consultation or generate an AI room design."
        path="/terms"
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2">
            <ScrollText size={14} /> Terms of Service
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[0.95]">
            The fine print, kept short.
          </h1>
          <p className="text-[#143D63]/70 mt-6 leading-relaxed">
            These terms apply when you use <span className="font-semibold">thezebo.com</span>,
            book a free consultation or use our AI Studio. Last updated: {updated}.
          </p>
        </Reveal>

        <div className="mt-14 space-y-12 text-[#143D63]/80 leading-relaxed">
          <Section title="1. Who we are">
            <p>
              {BRAND.full} (“ZEBO”) is an interior design and modular manufacturing studio based in Kolkata, India. This website is operated by ZEBO. By using it you agree to these terms.
            </p>
          </Section>

          <Section title="2. Free design consultation">
            <p>
              Consultations booked via <Link to="/consultation" className="text-[#8B0000] link-underline">/consultation</Link> are free and non-binding. Submitting a consultation form does <b>not</b> create a design contract. A separate signed proposal will be issued before any paid work begins.
            </p>
            <p className="mt-3">
              We aim to respond within 1 business day but cannot guarantee response times during holidays or peak demand.
            </p>
          </Section>

          <Section title="3. Quotes, timelines & pricing">
            <p>
              Any prices, timelines or renders shared before a signed proposal are indicative only. Final scope, materials, price and timeline will be set out in your signed project agreement.
            </p>
          </Section>

          <Section title="4. AI Studio use">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                AI-generated images are <b>concept visualisations</b>, not exact previews of what will be built. Real materials, lighting and dimensions may differ.
              </li>
              <li>
                You may download and use your AI Studio results for personal, non-commercial reference (e.g. sharing with your family or your designer).
              </li>
              <li>
                You may <b>not</b> republish AI Studio outputs commercially or claim them as your own original work, and you may not use the tool to generate unlawful, hateful or infringing imagery.
              </li>
              <li>
                We may throttle or block usage that appears automated or abusive.
              </li>
            </ul>
          </Section>

          <Section title="5. Photos, portfolio & reviews">
            <p>
              Gallery photos and case studies on this site are examples of our work or representative reference imagery. Individual project photos are shared with client permission where identifiable.
            </p>
            <p className="mt-3">
              If you post a Google review, it appears publicly on our Google Business Profile and is governed by Google&apos;s terms.
            </p>
          </Section>

          <Section title="6. Website content & IP">
            <p>
              All text, design, logos and photography on <span className="font-semibold">thezebo.com</span> are owned by {BRAND.full} or used under licence. Please don&apos;t copy or republish them without our written permission.
            </p>
          </Section>

          <Section title="7. Third-party links">
            <p>
              Our site may link to Google Maps, WhatsApp, social media and Google reviews. Those services have their own terms and privacy policies which we do not control.
            </p>
          </Section>

          <Section title="8. Acceptable use">
            <p>
              Don&apos;t use the site to attempt to break into it, run automated scrapers, upload malicious content, or submit false leads. We may block traffic that we reasonably believe is abusive.
            </p>
          </Section>

          <Section title="9. Disclaimers">
            <p>
              The website is provided “as is”. To the fullest extent permitted by law, ZEBO disclaims implied warranties. We take reasonable care but cannot guarantee that the site is uninterrupted or error-free.
            </p>
          </Section>

          <Section title="10. Limitation of liability">
            <p>
              To the extent permitted by law, ZEBO&apos;s total liability for any claim arising from your use of the website is limited to ₹1,000. Nothing in these terms limits liability that cannot be excluded under Indian law (e.g. death or personal injury caused by negligence, or fraud).
            </p>
          </Section>

          <Section title="11. Governing law & jurisdiction">
            <p>
              These terms are governed by the laws of India. Disputes will be subject to the exclusive jurisdiction of the courts of Kolkata, West Bengal.
            </p>
          </Section>

          <Section title="12. Changes">
            <p>
              We may update these terms as our services evolve. The “Last updated” date will reflect any change. Continued use of the website after an update means you accept the revised terms.
            </p>
          </Section>

          <Section title="13. Contact">
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Mail size={16} className="text-[#8B0000] mt-1" />
                <a href={`mailto:${BRAND.email}`} className="link-underline">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="text-[#8B0000] mt-1" />
                <a href={`tel:${BRAND.phoneRaw}`} className="link-underline">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-[#8B0000] mt-1" />
                <span>{BRAND.address}</span>
              </li>
            </ul>
          </Section>

          <div className="pt-8 border-t border-[#143D63]/10 flex items-center justify-between flex-wrap gap-3">
            <Link
              to="/"
              data-testid="terms-back-home"
              className="text-[#8B0000] font-semibold link-underline"
            >
              &larr; Back to home
            </Link>
            <Link
              to="/privacy"
              data-testid="terms-privacy-link"
              className="text-[#143D63] font-semibold link-underline"
            >
              Privacy Policy &rarr;
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section data-testid={`terms-section-${title.split(".")[0].trim()}`}>
      <h2 className="font-serif text-2xl sm:text-3xl text-[#143D63] font-light mb-4">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
